import { useCallback, useEffect, useRef } from 'react'

/**
 * A stable function identity that always calls the latest handler.
 *
 * For subscriptions -- keydown listeners, socket events, debounce timers -- where a
 * caller passes an inline closure. Without this, the effect that registers the
 * listener would either re-run on every render (tearing down and re-adding, and
 * dropping events in the gap) or capture a stale closure over state.
 *
 * The ref is updated in an effect rather than during render, because a render can be
 * discarded and a ref written during one is not rolled back. Every consumer here
 * calls the result from an event, which by definition happens after effects have
 * flushed, so there is no window where the ref is behind.
 */
export function useEventCallback<TArgs extends unknown[], TResult>(handler: (...args: TArgs) => TResult) {
    const ref = useRef(handler)

    useEffect(() => {
        ref.current = handler
    })

    return useCallback((...args: TArgs) => ref.current(...args), [])
}
